/**
 * Definição do fluxo do chat de pós-graduação (HN-3170) — dados, não
 * código. As 4 perguntas são as mesmas já existentes no formulário atual
 * (`ModalBankSlip`): nome, e-mail, telefone e "Você já é graduado?".
 */

import {
  GRADUATE_ANSWER_OPTIONS,
  type GraduateAnswer,
} from '@/helpers/postgraduate-agendor-funnel'
import type { Validator } from './validation'
import {
  createOptionValidator,
  validateEmail,
  validateName,
  validatePhone,
  VALIDATION_MESSAGES,
} from './validation'

export type ChatAnswerType = 'text' | 'email' | 'phone' | 'options'

export type ChatStepId = 'name' | 'email' | 'phone' | 'graduate'

export type ChatAnswers = Partial<Record<ChatStepId, string>>

export interface ChatStepOption {
  readonly value: string
  readonly label: string
}

export interface ChatStep {
  readonly id: ChatStepId
  /** Mensagens que o atendente envia ao entrar no passo, em ordem. */
  readonly messages: readonly string[]
  readonly answerType: ChatAnswerType
  /** Presente apenas quando `answerType` é `options`. */
  readonly options?: readonly ChatStepOption[]
  readonly validate: Validator
  readonly errorMessage: string
}

/** Enviada na abertura, antes do botão de início — varia por atendente. */
export function buildGreetingMessages(attendantIntro: string): readonly string[] {
  return [
    attendantIntro,
    'Vou te fazer algumas perguntas rápidas para te direcionar certinho. Vamos começar?',
  ]
}

export const CHAT_START_LABEL = 'Sim, começar agora'

export const CHAT_COMPLETION_MESSAGE =
  'Perfeito, recebi suas informações! Agora é só continuar por aqui ou pelo WhatsApp que eu te ajudo com os próximos passos.'

/** Após "Ver programa completo": e-mail com a ementa enviado com sucesso. */
export const CHAT_PROGRAM_MATERIAL_SENT_MESSAGE =
  'Pronto! Enviei o programa completo da pós-graduação para o seu e-mail. Se não encontrar, dá uma olhada na caixa de spam.'

/** O lead já está no Agendor — só o e-mail do programa falhou. */
export const CHAT_PROGRAM_MATERIAL_FAILED_MESSAGE =
  'Não consegui enviar o programa completo por e-mail agora, mas nossa equipe já recebeu seu contato e vai te enviar em breve.'

const GRADUATE_QUESTION = 'Você já é graduado?'

const GRADUATE_OPTIONS: readonly ChatStepOption[] = GRADUATE_ANSWER_OPTIONS.map(
  option => ({ value: option.value, label: option.label })
)

const GRADUATE_OPTION_VALUES: readonly string[] = GRADUATE_OPTIONS.map(
  option => option.value
)

export const CHAT_FLOW: readonly ChatStep[] = [
  {
    id: 'name',
    messages: ['Qual seu nome?'],
    answerType: 'text',
    validate: validateName,
    errorMessage: VALIDATION_MESSAGES.name,
  },
  {
    id: 'email',
    messages: ['Qual seu e-mail?'],
    answerType: 'email',
    validate: validateEmail,
    errorMessage: VALIDATION_MESSAGES.email,
  },
  {
    id: 'phone',
    messages: ['Poderia me informar o seu número, com o DDD?'],
    answerType: 'phone',
    validate: validatePhone,
    errorMessage: VALIDATION_MESSAGES.phone,
  },
  {
    id: 'graduate',
    messages: [GRADUATE_QUESTION],
    answerType: 'options',
    options: GRADUATE_OPTIONS,
    validate: createOptionValidator(GRADUATE_OPTION_VALUES, GRADUATE_QUESTION),
    errorMessage: GRADUATE_QUESTION,
  },
]

export function getGraduateOptionLabel(value: string): string {
  return GRADUATE_OPTIONS.find(option => option.value === value)?.label ?? value
}

export type { GraduateAnswer }
